import type {
  Appendix,
  CodeItemGroup,
  CodeRow,
  ConditionEntry,
  ConditionGroup,
  IfNode,
  RuleNode,
  TextRuleNode,
} from '../types/codeIndex';
import { normalizeAppendixRefs, normalizeGroupAppendixRefs } from './appendixRefs';

const FORMAT_VALUE_FIELD_PATHS = new Set([
  'optionCode',
  'description',
  'rule',
  'regions',
  'status',
  'group',
  'position',
  'valuePosition',
  'valuePositionEnd',
  'appendixRefs',
]);

export type ChangeOperation = 'create' | 'update' | 'delete';

// Snapshot of a whole appendix (tables, columns, rows, cells) carried by a rolled-up
// appendix change. Mirrors the API's subtree JSON; a cell's `columnId` is its column's domain id.
// Code item cells carry the value they point at (valueRefId) resolved to its text when the change
// was rolled up; snapshots from before references existed only have `value` (hasValueRef false).
export interface AppendixCellSnapshot {
  id: string | null;
  value: string | null;
  notes: string | null;
  columnId: string | null;
  hasValueRef: boolean;
  valueRefId: string | null;
  optionCode: string | null;
  valueDescription: string | null;
  valueStatus: string | null;
}

export interface AppendixRowSnapshot {
  id: string | null;
  position: number;
  cells: AppendixCellSnapshot[];
}

export interface AppendixColumnSnapshot {
  id: string | null;
  name: string | null;
  title: string | null;
  dataType: string | null;
  length: number | null;
  unit: string | null;
  position: number;
  codeItemData: string | null;
  // What a code item column points at and, for snapshots rolled up with references, what that
  // code item said at roll-up. codeItemData is never displayed.
  codeItemKey: string | null;
  codeItemGroup: string | null;
  codeItemPosition: number | null;
  codeItemPositionEnd: number | null;
  codeItemCode: string | null;
  codeItemNumber: string | null;
  codeItemTitle: string | null;
  codeItemStatus: string | null;
}

export interface AppendixTableSnapshot {
  id: string | null;
  name: string | null;
  description: string | null;
  columns: AppendixColumnSnapshot[];
  rows: AppendixRowSnapshot[];
}

export interface AppendixSnapshot {
  definesValidCombinations?: boolean;
  id: string | null;
  title: string | null;
  description: string | null;
  tables: AppendixTableSnapshot[];
}

export type AppendixDiffStatus = 'added' | 'removed' | 'changed' | 'same';

export interface AppendixTableDiff {
  columns: Array<{ position: number; status: AppendixDiffStatus }>;
  rows: Array<{ position: number; status: AppendixDiffStatus; changedColumnPositions: number[] }>;
}

export type ParsedChangeValue =
  | {
      kind: 'code-item';
      group: CodeItemGroup;
    }
  | {
      kind: 'appendix';
      appendix: AppendixSnapshot;
    }
  | {
      kind: 'format-value';
      row: CodeRow;
      code: string | null;
    }
  | {
      kind: 'rule';
      rule: RuleNode | null;
    }
  | {
      kind: 'regions';
      regions: string[];
    }
  | {
      // Appendix domain ids referenced by a code item or value.
      kind: 'appendixRefs';
      refs: string[];
    }
  | {
      kind: 'scalar';
      value: string | number | boolean | null;
    }
  | {
      kind: 'raw';
      raw: string | null;
      pretty: string | null;
    };

export interface ChangePreviewInput {
  operation: ChangeOperation;
  nodeType: string;
  nodeId?: string | null;
  path?: string | null;
  oldValue?: string | null;
  newValue?: string | null;
  // "B — Fan curves": the letter is only known on the codesheet page, which resolves it by key.
  appendixLabel?: string | null;
}

export interface ChangePreviewModel extends ChangePreviewInput {
  summary: string;
  fieldLabel: string | null;
  oldPreview: ParsedChangeValue | null;
  newPreview: ParsedChangeValue | null;
}

function safeString(value: unknown): string {
  return typeof value === 'string' ? value : '';
}

function normalizeOptionalString(value: unknown): string | null {
  const normalized = safeString(value).trim();
  return normalized ? normalized : null;
}

function toFiniteNumber(value: unknown, fallback: number): number {
  const numericValue = Number(value);
  return Number.isFinite(numericValue) ? numericValue : fallback;
}

function isPlainObject(value: unknown): value is Record<string, any> {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
}

function safeJsonParse(raw: string): { valid: boolean; parsed: unknown } {
  try {
    return {
      valid: true,
      parsed: JSON.parse(raw),
    };
  } catch {
    return {
      valid: false,
      parsed: raw,
    };
  }
}

function prettifyParsedValue(value: unknown): string | null {
  if (value === undefined) return null;
  if (typeof value === 'string') return value;

  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
}

function toNullableString(value: unknown): string | null {
  if (value === null || value === undefined) return null;
  return typeof value === 'string' ? value : String(value);
}

function toNullableNumber(value: unknown): number | null {
  if (value === null || value === undefined || value === '') return null;
  const numericValue = Number(value);
  return Number.isFinite(numericValue) ? numericValue : null;
}

function byPosition<T extends { position: number }>(a: T, b: T): number {
  return a.position - b.position;
}

export function normalizeAppendixSnapshot(value: unknown): AppendixSnapshot | null {
  if (!isPlainObject(value) || !Array.isArray(value.tables)) return null;

  const tables = value.tables.filter(isPlainObject).map((table): AppendixTableSnapshot => {
    const columns = (Array.isArray(table.columns) ? table.columns : [])
      .filter(isPlainObject)
      .map((column, index): AppendixColumnSnapshot => ({
        id: normalizeOptionalString(column.id),
        name: toNullableString(column.name),
        title: toNullableString(column.title),
        dataType: toNullableString(column.dataType),
        length: toNullableNumber(column.length),
        unit: toNullableString(column.unit),
        position: toFiniteNumber(column.position, index),
        codeItemData: toNullableString(column.codeItemData),
        codeItemKey: normalizeOptionalString(column.codeItemKey),
        codeItemGroup: toNullableString(column.codeItemGroup),
        codeItemPosition: toNullableNumber(column.codeItemPosition),
        codeItemPositionEnd: toNullableNumber(column.codeItemPositionEnd),
        codeItemCode: toNullableString(column.codeItemCode),
        codeItemNumber: toNullableString(column.codeItemNumber),
        codeItemTitle: toNullableString(column.codeItemTitle),
        codeItemStatus: toNullableString(column.codeItemStatus),
      }))
      .sort(byPosition);
    const rows = (Array.isArray(table.rows) ? table.rows : [])
      .filter(isPlainObject)
      .map((row, index): AppendixRowSnapshot => ({
        id: normalizeOptionalString(row.id),
        position: toFiniteNumber(row.position, index),
        cells: (Array.isArray(row.cells) ? row.cells : [])
          .filter(isPlainObject)
          .map((cell): AppendixCellSnapshot => ({
            id: normalizeOptionalString(cell.id),
            value: toNullableString(cell.value),
            notes: toNullableString(cell.notes),
            columnId: normalizeOptionalString(cell.columnId),
            hasValueRef: 'valueRefId' in cell,
            valueRefId: normalizeOptionalString(cell.valueRefId),
            optionCode: toNullableString(cell.optionCode),
            valueDescription: toNullableString(cell.valueDescription),
            valueStatus: toNullableString(cell.valueStatus),
          })),
      }))
      .sort(byPosition);

    return {
      id: normalizeOptionalString(table.id),
      name: toNullableString(table.name),
      description: toNullableString(table.description),
      columns,
      rows,
    };
  });

  return {
    id: normalizeOptionalString(value.id),
    title: toNullableString(value.title),
    description: toNullableString(value.description),
    definesValidCombinations: value.definesValidCombinations === true,
    tables,
  };
}

// Cells of a row keyed by column position: matched through the cell's column id, else by index.
export function resolveAppendixRowCells(
  table: AppendixTableSnapshot,
  row: AppendixRowSnapshot
): Map<number, AppendixCellSnapshot> {
  const cells = new Map<number, AppendixCellSnapshot>();
  row.cells.forEach((cell, index) => {
    const column = (cell.columnId ? table.columns.find((candidate) => candidate.id === cell.columnId) : undefined)
      ?? table.columns[index];
    if (column && !cells.has(column.position)) cells.set(column.position, cell);
  });
  return cells;
}

function normalizeCellText(value: string | null | undefined): string {
  return safeString(value).trim();
}

function columnSignature(column: AppendixColumnSnapshot): string {
  return [
    column.title,
    column.name,
    column.unit,
    column.dataType,
    column.codeItemKey,
    column.codeItemGroup,
    column.codeItemPosition === null ? '' : String(column.codeItemPosition),
    column.codeItemPositionEnd === null ? '' : String(column.codeItemPositionEnd),
    // Only columns saved before references compare by code; a renamed code item is not a change.
    column.codeItemKey ? '' : column.codeItemData,
  ].map(normalizeCellText).join('\u0000');
}

export const APPENDIX_MISSING_VALUE_TEXT = 'No longer exists';

// What a snapshot cell shows: the value's text as resolved when the change was rolled up (the
// fragment on a composite code item), never the text it was picked with. Snapshots from before
// references fall back to the stored value.
export function getAppendixCellDisplayText(cell: AppendixCellSnapshot | null | undefined): string {
  if (!cell) return '';
  if (cell.hasValueRef && cell.valueRefId) {
    return cell.valueStatus === 'Missing' ? APPENDIX_MISSING_VALUE_TEXT : safeString(cell.optionCode);
  }
  return safeString(cell.value);
}

// Code item cells compare by the value they point at, so a renamed value is not a change.
function appendixCellsDiffer(before: AppendixCellSnapshot | undefined, after: AppendixCellSnapshot | undefined): boolean {
  if (normalizeCellText(before?.notes) !== normalizeCellText(after?.notes)) return true;
  if (before?.hasValueRef && after?.hasValueRef) {
    return normalizeCellText(before.valueRefId) !== normalizeCellText(after.valueRefId)
      || normalizeCellText(before.value) !== normalizeCellText(after.value);
  }
  return normalizeCellText(getAppendixCellDisplayText(before)) !== normalizeCellText(getAppendixCellDisplayText(after));
}

// "C16", "018 — 1 Outdoor air" for a composite position range, or that the code item no longer
// exists; null for other columns. Never the code the column was saved with.
export function getAppendixColumnCodeItemText(column: AppendixColumnSnapshot): string | null {
  if (column.dataType !== 'CodeItem' || !column.codeItemKey) return null;
  if (column.codeItemStatus === 'Missing') return 'Code item no longer exists';
  const code = safeString(column.codeItemCode);
  if (column.codeItemPosition === null) return code || null;
  const end = column.codeItemPositionEnd ?? column.codeItemPosition;
  const range = `${column.codeItemPosition + 1}${end !== column.codeItemPosition ? `–${end + 1}` : ''}`;
  const position = `${range}${column.codeItemGroup ? ` ${column.codeItemGroup}` : ''}`;
  return code ? `${code} — ${position}` : position;
}

// Compares two snapshots of the same table: columns and rows are matched by position, cells by
// column position. A column or row only on one side is added/removed; a row whose cells differ is changed.
export function diffAppendixTables(
  before: AppendixTableSnapshot | null | undefined,
  after: AppendixTableSnapshot | null | undefined
): AppendixTableDiff {
  const beforeColumns = new Map((before?.columns ?? []).map((column) => [column.position, column]));
  const afterColumns = new Map((after?.columns ?? []).map((column) => [column.position, column]));
  const columnPositions = Array.from(new Set(Array.from(beforeColumns.keys()).concat(Array.from(afterColumns.keys())))).sort((a, b) => a - b);
  const columns = columnPositions.map((position) => {
    const beforeColumn = beforeColumns.get(position);
    const afterColumn = afterColumns.get(position);
    const status: AppendixDiffStatus = !beforeColumn
      ? 'added'
      : !afterColumn
        ? 'removed'
        : columnSignature(beforeColumn) === columnSignature(afterColumn) ? 'same' : 'changed';
    return { position, status };
  });
  const sharedColumnPositions = columns.filter((column) => column.status !== 'added' && column.status !== 'removed').map((column) => column.position);

  const beforeRows = new Map((before?.rows ?? []).map((row) => [row.position, row]));
  const afterRows = new Map((after?.rows ?? []).map((row) => [row.position, row]));
  const rowPositions = Array.from(new Set(Array.from(beforeRows.keys()).concat(Array.from(afterRows.keys())))).sort((a, b) => a - b);
  const rows = rowPositions.map((position) => {
    const beforeRow = beforeRows.get(position);
    const afterRow = afterRows.get(position);
    if (!beforeRow || !before) return { position, status: 'added' as const, changedColumnPositions: [] };
    if (!afterRow || !after) return { position, status: 'removed' as const, changedColumnPositions: [] };

    const beforeCells = resolveAppendixRowCells(before, beforeRow);
    const afterCells = resolveAppendixRowCells(after, afterRow);
    const changedColumnPositions = sharedColumnPositions.filter((columnPosition) => {
      const beforeCell = beforeCells.get(columnPosition);
      const afterCell = afterCells.get(columnPosition);
      return appendixCellsDiffer(beforeCell, afterCell);
    });
    return {
      position,
      status: changedColumnPositions.length > 0 ? 'changed' as const : 'same' as const,
      changedColumnPositions,
    };
  });

  return { columns, rows };
}

function pluralize(count: number, singular: string): string {
  return `${count} ${count === 1 ? singular : `${singular}s`}`;
}

// "(3 columns × 8 rows)" for the first table of the snapshot.
export function describeAppendixSnapshotSize(snapshot: AppendixSnapshot | null | undefined): string | null {
  const table = snapshot?.tables[0];
  if (!table) return null;
  return `${pluralize(table.columns.length, 'column')} × ${pluralize(table.rows.length, 'row')}`;
}

function normalizeAppendix(value: unknown): Appendix | null {
  if (!isPlainObject(value)) return null;

  return {
    id: normalizeOptionalString(value.id),
    title: normalizeOptionalString(value.title),
    description: normalizeOptionalString(value.description),
    content: normalizeOptionalString(value.content),
    attachment: normalizeOptionalString(value.attachment),
    entityId: normalizeOptionalString(value.entityId),
    parentEntityId: normalizeOptionalString(value.parentEntityId),
    createdAt: normalizeOptionalString(value.createdAt),
    updatedAt: normalizeOptionalString(value.updatedAt),
  };
}

function normalizeConditionEntry(value: unknown): ConditionEntry | null {
  if (!isPlainObject(value)) return null;

  const reference = normalizeOptionalString(value.reference);
  const condition = normalizeOptionalString(value.condition);
  const conditionValue = normalizeOptionalString(value.value);
  const from = normalizeOptionalString(value.from);
  const to = normalizeOptionalString(value.to);

  if (!reference || !condition) return null;
  if (condition === 'between') {
    if (!from || !to) return null;

    return {
      reference,
      condition,
      value: null,
      from,
      to,
    };
  }

  if (!conditionValue) return null;

  return {
    reference,
    condition,
    value: conditionValue,
    from: null,
    to: null,
  };
}

function normalizeConditionGroup(value: unknown): ConditionGroup | null {
  if (!isPlainObject(value)) return null;

  const logicalOperator = value.logicalOperator === 'AND' || value.logicalOperator === 'OR'
    ? value.logicalOperator
    : null;

  if (!logicalOperator) return null;

  const conditions = Array.isArray(value.conditions)
    ? value.conditions
        .map(normalizeConditionEntry)
        .filter((entry): entry is ConditionEntry => Boolean(entry))
    : null;

  const groups = Array.isArray(value.groups)
    ? value.groups
        .map(normalizeConditionGroup)
        .filter((group): group is ConditionGroup => Boolean(group))
    : null;

  return {
    logicalOperator,
    conditions,
    groups,
  };
}

function normalizeRuleNode(value: unknown): RuleNode | null {
  if (value === null) return null;
  if (!isPlainObject(value)) return null;

  const type = normalizeOptionalString(value.type);
  const description = normalizeOptionalString(value.description);
  const freeText = normalizeOptionalString(value.freeText);
  const appendix = normalizeAppendix(value.appendix);
  const id = normalizeOptionalString(value.id);

  if (type === 'while') {
    const reference = normalizeOptionalString(value.reference);
    const condition = normalizeOptionalString(value.condition);
    const ruleValue = normalizeOptionalString(value.value);

    if (!reference || !condition || !ruleValue) return null;

    return {
      type: 'while',
      id,
      description,
      freeText,
      appendix,
      reference,
      condition,
      value: ruleValue,
    };
  }

  if (type === 'if') {
    const parsedIf = normalizeRuleNode(value.if);
    const parsedElse = normalizeRuleNode(value.else);
    const consequenceGroups = Array.isArray(value.consequenceGroups)
      ? value.consequenceGroups
          .map(normalizeConditionGroup)
          .filter((group): group is ConditionGroup => Boolean(group))
      : null;

    return {
      type: 'if',
      id,
      description,
      freeText,
      appendix,
      reference: normalizeOptionalString(value.reference),
      condition: normalizeOptionalString(value.condition),
      value: normalizeOptionalString(value.value),
      returnValue: normalizeOptionalString(value.returnValue),
      conditionGroup: normalizeConditionGroup(value.conditionGroup),
      consequenceGroups,
      future: typeof value.future === 'boolean'
        ? value.future
        : (typeof value.isFuture === 'boolean' ? value.isFuture : null),
      if: parsedIf && parsedIf.type === 'if' ? parsedIf as IfNode : null,
      else: parsedElse && parsedElse.type === 'if' ? parsedElse as IfNode : null,
    };
  }

  if (type === 'text') {
    return {
      type: 'text',
      id,
      description,
      freeText,
      appendix,
    } satisfies TextRuleNode;
  }

  return null;
}

function normalizeCodeRow(value: unknown, index: number): CodeRow | null {
  if (!isPlainObject(value)) return null;

  const optionCode = safeString(value.optionCode);
  const rowId = normalizeOptionalString(value.rowId)
    || normalizeOptionalString(value.id)
    || normalizeOptionalString(optionCode)
    || `row-${index}`;

  const descriptionBlockId = normalizeOptionalString(value.descriptionBlockId) || rowId;
  const availabilityBlockId = normalizeOptionalString(value.availabilityBlockId) || rowId;

  return {
    id: normalizeOptionalString(value.id),
    rowId,
    optionCode,
    optionLabel: normalizeOptionalString(value.optionLabel) || undefined,
    status: value.status === null ? null : safeString(value.status),
    group: normalizeOptionalString(value.group),
    valuePosition: value.valuePosition === null || value.valuePosition === undefined
      ? null
      : toFiniteNumber(value.valuePosition, 0),
    valuePositionEnd: value.valuePositionEnd === null || value.valuePositionEnd === undefined
      ? null
      : toFiniteNumber(value.valuePositionEnd, 0),
    position: toFiniteNumber(value.position, index),
    description: value.description === null ? null : safeString(value.description),
    descriptionBlockId,
    availabilityBlockId,
    regions: Array.isArray(value.regions)
      ? value.regions.map((region) => safeString(region).trim()).filter(Boolean)
      : [],
    isNew: typeof value.isNew === 'boolean' ? value.isNew : undefined,
    rule: normalizeRuleNode(value.rule),
    appendix: normalizeAppendix(value.appendix),
    appendixRefs: normalizeAppendixRefs(value.appendixRefs),
  };
}

function normalizeCodeItemGroup(value: unknown): CodeItemGroup | null {
  if (!isPlainObject(value)) return null;

  const code = normalizeOptionalString(value.code);
  if (!code) return null;

  const posRangeValues = Array.isArray(value.posRange) ? value.posRange : [];
  const start = toFiniteNumber(posRangeValues[0], 1);
  const end = toFiniteNumber(posRangeValues[1], start);

  const rows = Array.isArray(value.rows)
    ? value.rows
        .map((row, index) => normalizeCodeRow(row, index))
        .filter((row): row is CodeRow => Boolean(row))
    : [];

  return {
    id: normalizeOptionalString(value.id),
    localKey: normalizeOptionalString(value.localKey) || undefined,
    codeItemPos: {
      code,
      posRange: [start, end],
      label: `${code} [${start}-${end}]`,
      isDefault: Boolean(value.isDefault),
    },
    title: normalizeOptionalString(value.title),
    appendixRefs: normalizeAppendixRefs(value.appendixRefs),
    groupAppendixRefs: normalizeGroupAppendixRefs(value.groupAppendixRefs),
    rows,
  };
}

function normalizeFormatValue(value: unknown, context: { path?: string | null; nodeId?: string | null }): ParsedChangeValue | null {
  const row = normalizeCodeRow(value, 0);
  if (!row) return null;

  return {
    kind: 'format-value',
    row,
    code: inferFormatValueCode(row, context.path, context.nodeId),
  };
}

function extractCodeFromRowIdentifier(value: string | null | undefined): string | null {
  const normalized = safeString(value).trim();
  if (!normalized) return null;

  const match = normalized.match(/^([A-Za-z]?\d{2,})[-_]/);
  return match?.[1] || null;
}

function extractOptionFromRowIdentifier(value: string | null | undefined): string | null {
  const normalized = safeString(value).trim();
  if (!normalized) return null;

  const separator = normalized.includes('-') ? '-' : (normalized.includes('_') ? '_' : null);
  if (!separator) return null;

  const [, secondToken] = normalized.split(separator);
  if (!secondToken || secondToken.toUpperCase() === 'NEW') return null;
  return secondToken;
}

function inferFormatValueCode(row: CodeRow, path?: string | null, nodeId?: string | null): string | null {
  const fromRow = extractCodeFromRowIdentifier(row.rowId);
  if (fromRow) return fromRow;

  const normalizedPath = safeString(path).trim();
  if (normalizedPath && !FORMAT_VALUE_FIELD_PATHS.has(normalizedPath)) {
    return normalizedPath;
  }

  return extractCodeFromRowIdentifier(nodeId);
}

function isCodeItemNodeType(nodeType: string): boolean {
  return nodeType === 'CodeItemNode' || nodeType === 'CodeItem';
}

export function formatChangeFieldLabel(path: string | null | undefined): string | null {
  const normalizedPath = safeString(path).trim();
  if (!normalizedPath) return null;

  if (normalizedPath === 'name') return 'Code';
  if (normalizedPath === 'optionCode') return 'Option Code';
  if (normalizedPath === 'position') return 'Position';
  if (normalizedPath === 'valuePosition') return 'Value Position';
  if (normalizedPath === 'valuePositionEnd') return 'Value Position End';
  if (normalizedPath === 'group') return 'Group';
  if (normalizedPath === 'len') return 'Length';
  if (normalizedPath === 'description') return 'Description';
  if (normalizedPath === 'rule') return 'Availability Rule';
  if (normalizedPath === 'regions') return 'Regions';
  if (normalizedPath === 'status') return 'Status';
  if (normalizedPath === 'title') return 'Title';
  if (normalizedPath === 'defines_valid_combinations') return 'Valid combinations';
  if (normalizedPath === 'appendixRefs' || normalizedPath === 'appendix_refs') return 'Appendix references';
  if (normalizedPath === 'groupAppendixRefs' || normalizedPath === 'group_appendix_refs') return 'Group appendix references';

  return normalizedPath
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/\b\w/g, (character) => character.toUpperCase());
}

function formatNodeLabel(nodeType: string): string {
  if (isCodeItemNodeType(nodeType)) return 'code item';
  if (nodeType === 'FormatValue') return 'option';

  return safeString(nodeType)
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/[_-]+/g, ' ')
    .trim()
    .toLowerCase();
}

export function mapApiChangeTypeToOperation(changeType: number | string | null | undefined): ChangeOperation {
  if (typeof changeType === 'number') {
    if (changeType === 1) return 'create';
    if (changeType === 3) return 'delete';
    return 'update';
  }

  const normalized = safeString(changeType).trim().toLowerCase();
  if (normalized === 'create' || normalized === 'created') return 'create';
  if (normalized === 'delete' || normalized === 'deleted') return 'delete';
  return 'update';
}

export function parseChangeValue(
  rawValue: string | null | undefined,
  context: Pick<ChangePreviewInput, 'nodeType' | 'path' | 'nodeId'>
): ParsedChangeValue | null {
  if (rawValue === null || rawValue === undefined) return null;

  const parsedResult = safeJsonParse(rawValue);
  if (!parsedResult.valid) {
    return {
      kind: 'raw',
      raw: rawValue,
      pretty: rawValue,
    };
  }

  const parsedValue = parsedResult.parsed;

  if (isCodeItemNodeType(context.nodeType)) {
    const codeItemGroup = normalizeCodeItemGroup(parsedValue);
    if (codeItemGroup) {
      return {
        kind: 'code-item',
        group: codeItemGroup,
      };
    }
  }

  if (context.nodeType === 'Appendix') {
    const appendix = normalizeAppendixSnapshot(parsedValue);
    if (appendix) {
      return {
        kind: 'appendix',
        appendix,
      };
    }
  }

  if (context.nodeType === 'FormatValue' && safeString(context.path).trim() === 'rule') {
    const rule = normalizeRuleNode(parsedValue);
    return {
      kind: 'rule',
      rule,
    };
  }

  if (context.nodeType === 'FormatValue' && safeString(context.path).trim() === 'regions' && Array.isArray(parsedValue)) {
    return {
      kind: 'regions',
      regions: parsedValue.map((region) => safeString(region).trim()).filter(Boolean),
    };
  }

  if (safeString(context.path).trim() === 'appendixRefs' && Array.isArray(parsedValue)) {
    return {
      kind: 'appendixRefs',
      refs: normalizeAppendixRefs(parsedValue),
    };
  }

  if (
    parsedValue === null
    || typeof parsedValue === 'string'
    || typeof parsedValue === 'number'
    || typeof parsedValue === 'boolean'
  ) {
    return {
      kind: 'scalar',
      value: parsedValue,
    };
  }

  if (context.nodeType === 'FormatValue') {
    const formatValue = normalizeFormatValue(parsedValue, context);
    if (formatValue) return formatValue;
  }

  return {
    kind: 'raw',
    raw: rawValue,
    pretty: prettifyParsedValue(parsedValue),
  };
}

export function buildChangeDescription(input: ChangePreviewInput): string {
  const oldPreview = parseChangeValue(input.oldValue, input);
  const newPreview = parseChangeValue(input.newValue, input);
  const codeItemPreview = newPreview?.kind === 'code-item' ? newPreview : (oldPreview?.kind === 'code-item' ? oldPreview : null);
  const formatValuePreview = newPreview?.kind === 'format-value'
    ? newPreview
    : (oldPreview?.kind === 'format-value' ? oldPreview : null);

  if (isCodeItemNodeType(input.nodeType)) {
    const codeFromNameUpdate = safeString(input.path).trim() === 'name'
      ? (
          newPreview?.kind === 'scalar'
            ? safeString(newPreview.value)
            : oldPreview?.kind === 'scalar'
              ? safeString(oldPreview.value)
              : ''
        ).trim()
      : '';
    const code = codeItemPreview?.group.codeItemPos.code || codeFromNameUpdate || normalizeOptionalString(input.nodeId);

    if (input.operation === 'create') return code ? `Added code item ${code}` : 'Added code item';
    if (input.operation === 'delete') return code ? `Removed code item ${code}` : 'Removed code item';

    const fieldLabel = formatChangeFieldLabel(input.path);
    if (fieldLabel && code) return `Updated ${fieldLabel.toLowerCase()} for code item ${code}`;
    return code ? `Updated code item ${code}` : 'Updated code item';
  }

  if (input.nodeType === 'FormatValue') {
    const optionCode = formatValuePreview?.row.optionCode || extractOptionFromRowIdentifier(input.nodeId);
    const code = formatValuePreview?.code || extractCodeFromRowIdentifier(input.nodeId);
    const optionLabel = optionCode ? `option ${optionCode}` : 'option';

    if (input.operation === 'create') {
      return code ? `Added ${optionLabel} to code item ${code}` : `Added ${optionLabel}`;
    }

    if (input.operation === 'delete') {
      return code ? `Removed ${optionLabel} from code item ${code}` : `Removed ${optionLabel}`;
    }

    const fieldLabel = formatChangeFieldLabel(input.path);
    if (fieldLabel && optionCode) return `Updated ${fieldLabel.toLowerCase()} for option ${optionCode}`;
    if (fieldLabel) return `Updated ${fieldLabel.toLowerCase()}`;
    if (optionCode) return `Updated option ${optionCode}`;
    return 'Updated option';
  }

  const action = input.operation === 'create'
    ? 'Added'
    : input.operation === 'delete'
      ? 'Removed'
      : 'Updated';

  if (input.nodeType === 'Appendix' && input.path === 'defines_valid_combinations') {
    const enabled = safeJsonParse(input.newValue || 'false').parsed === true;
    const label = normalizeOptionalString(input.appendixLabel) || input.nodeId || '';
    return `${enabled ? 'Marked' : 'Unmarked'} appendix ${label} ${enabled ? 'as defining' : 'as no longer defining'} valid combinations`;
  }

  if (input.nodeType === 'Appendix') {
    const snapshot = newPreview?.kind === 'appendix'
      ? newPreview.appendix
      : (oldPreview?.kind === 'appendix' ? oldPreview.appendix : null);
    const label = normalizeOptionalString(input.appendixLabel)
      || normalizeOptionalString(snapshot?.title)
      || normalizeOptionalString(input.nodeId);
    const size = describeAppendixSnapshotSize(snapshot);
    return `${action} appendix${label ? ` ${label}` : ''}${size ? ` (${size})` : ''}`;
  }

  const fieldLabel = input.operation === 'update'
    ? formatChangeFieldLabel(input.path)
    : null;

  if (fieldLabel) return `${action} ${fieldLabel.toLowerCase()}`;
  return `${action} ${formatNodeLabel(input.nodeType)}`;
}

export function buildChangePreviewModel(input: ChangePreviewInput): ChangePreviewModel {
  return {
    ...input,
    summary: buildChangeDescription(input),
    fieldLabel: formatChangeFieldLabel(input.path),
    oldPreview: parseChangeValue(input.oldValue, input),
    newPreview: parseChangeValue(input.newValue, input),
  };
}
